import React, { useEffect, useState } from "react";
import {
  Card,
  Title,
  Stack,
  Grid,
  NumberInput,
  MultiSelect,
  Text,
  Button,
  Group,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

const DEFAULT_SETTINGS = {
  training: { minutes_weight: 20, courses_weight: 10, course_ids: [] },
  programs_completed: { weight: 25 },
  meetings_attended: { manager_weight: 15, team_weight: 10 },
  pitch_reviews_sent: {
    quantity_weight: 10,
    frequency_weight: 10,
    window_days: 7,
  },
};

function RepHealthSettingsCard() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);

  const fetchCourses = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/training/courses/company"),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load training courses");
      }

      const result = await response.json();
      const options = (result.data || []).map(course => ({
        value: String(course.id),
        label: course.course_title || `Course ${course.id}`,
      }));
      setCourseOptions(options);
    } catch (error) {
      showError(error.message || "Failed to load training courses");
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/company/rep-health-settings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load rep health settings");
      }

      const result = await response.json();
      setSettings(result.data || DEFAULT_SETTINGS);
    } catch (error) {
      showError(error.message || "Failed to load rep health settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateNestedValue = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/company/rep-health-settings"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(settings),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to save rep health settings");
      }

      const result = await response.json();
      setSettings(result.data || settings);
      showSuccess("Rep health settings saved successfully!");
    } catch (error) {
      showError(error.message || "Failed to save rep health settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>Rep Health Settings</Title>
            <Text size="sm" c="dimmed">
              Set how rep health is scored for managers in downline management.
            </Text>
          </div>
          <Button onClick={handleSave} loading={saving} disabled={loading}>
            Save Settings
          </Button>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text fw={600}>Training</Text>
              <NumberInput
                label="Minutes Watched Weight"
                min={0}
                value={settings.training.minutes_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "training",
                    "minutes_weight",
                    Number(value) || 0
                  )
                }
              />
              <NumberInput
                label="Courses Completed Weight"
                min={0}
                value={settings.training.courses_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "training",
                    "courses_weight",
                    Number(value) || 0
                  )
                }
              />
              <MultiSelect
                label="Courses to Grade On"
                placeholder="Leave blank to use all courses"
                data={courseOptions}
                value={(settings.training.course_ids || []).map(String)}
                searchable
                clearable
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "training",
                    "course_ids",
                    value.map(id => Number(id)).filter(Boolean)
                  )
                }
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text fw={600}>Programs Completed</Text>
              <NumberInput
                label="Weight"
                min={0}
                value={settings.programs_completed.weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "programs_completed",
                    "weight",
                    Number(value) || 0
                  )
                }
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text fw={600}>Meetings Attended</Text>
              <NumberInput
                label="Manager Meetings Weight"
                min={0}
                value={settings.meetings_attended.manager_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "meetings_attended",
                    "manager_weight",
                    Number(value) || 0
                  )
                }
              />
              <NumberInput
                label="Team Meetings Weight"
                min={0}
                value={settings.meetings_attended.team_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "meetings_attended",
                    "team_weight",
                    Number(value) || 0
                  )
                }
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Text fw={600}>Pitch Reviews Sent</Text>
              <NumberInput
                label="Quantity Weight"
                min={0}
                value={settings.pitch_reviews_sent.quantity_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "pitch_reviews_sent",
                    "quantity_weight",
                    Number(value) || 0
                  )
                }
              />
              <NumberInput
                label="Frequency Weight"
                min={0}
                value={settings.pitch_reviews_sent.frequency_weight}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "pitch_reviews_sent",
                    "frequency_weight",
                    Number(value) || 0
                  )
                }
              />
              <NumberInput
                label="Frequency Window (days)"
                min={1}
                value={settings.pitch_reviews_sent.window_days}
                disabled={loading}
                onChange={value =>
                  updateNestedValue(
                    "pitch_reviews_sent",
                    "window_days",
                    Number(value) || 1
                  )
                }
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Card>
  );
}

export default RepHealthSettingsCard;
